import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Button,
  Chip,
  Input,
  Label,
  Pagination,
  Spinner,
  Table,
  TextField,
} from "@heroui/react";
import { dt } from "@/shared/helper/datetime";

import { FocusRecord, useFocusList } from "@/features/focus/use-focus.ts";

export const Route = createFileRoute("/dashboard/focus")({
  component: RouteComponent,
});

const LIMIT = 20;

function RouteComponent() {
  const [userId, setUserId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [page, setPage] = useState(1);

  const { data, isFetching } = useFocusList({
    userId: userId.trim() || undefined,
    orderId: orderId.trim() || undefined,
    page,
    limit: LIMIT,
  });

  const records = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const resetFilters = () => {
    setUserId("");
    setOrderId("");
    setPage(1);
  };

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row gap-2 justify-between h-12 items-center">
        <p className="font-bold text-xl">Focus</p>
        <p className="text-sm text-neutral-500">Всего: {total}</p>
      </div>

      <div className="flex flex-row flex-wrap gap-2 items-end mt-2">
        <TextField className="w-64">
          <Label>User ID</Label>
          <Input
            value={userId}
            placeholder="Фильтр по User ID"
            onChange={(e) => {
              setUserId(e.target.value);
              setPage(1);
            }}
          />
        </TextField>

        <TextField className="w-64">
          <Label>Order ID</Label>
          <Input
            value={orderId}
            placeholder="Фильтр по Order ID"
            onChange={(e) => {
              setOrderId(e.target.value);
              setPage(1);
            }}
          />
        </TextField>

        {(userId || orderId) && (
          <Button variant="outline" onPress={resetFilters}>
            Сбросить
          </Button>
        )}
      </div>

      {isFetching ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : records.length === 0 ? (
        <div className="flex justify-center py-8 text-neutral-500">
          Записи не найдены
        </div>
      ) : (
        <Table.Root className="mt-4">
          <Table.Content aria-label="Focus записи">
            <Table.Header>
              <Table.Column>ID</Table.Column>
              <Table.Column>Дата</Table.Column>
              <Table.Column>User ID</Table.Column>
              <Table.Column>Order ID</Table.Column>
              <Table.Column>Place ID</Table.Column>
              <Table.Column>Target</Table.Column>
              <Table.Column>Описание</Table.Column>
              <Table.Column>Identification</Table.Column>
            </Table.Header>
            <Table.Body items={records}>
              {(record: FocusRecord) => (
                <Table.Row id={String(record.id)}>
                  <Table.Cell className="font-mono text-sm">
                    {record.id}
                  </Table.Cell>
                  <Table.Cell className="text-sm text-neutral-600">
                    {dt(record.createdAt).format("DD.MM.YYYY HH:mm")}
                  </Table.Cell>
                  <Table.Cell className="font-mono text-xs">
                    {record.userId || "—"}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {record.orderId || "—"}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {record.orderPackageId ?? "—"}
                  </Table.Cell>
                  <Table.Cell>
                    {record.target ? (
                      <Chip color="accent" size="sm" variant="soft">
                        {record.target}
                      </Chip>
                    ) : (
                      "—"
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-sm text-neutral-600 max-w-xs">
                    {record.description || "—"}
                  </Table.Cell>
                  <Table.Cell className="font-mono text-xs text-neutral-500">
                    {record.identification || "—"}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.Root>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center py-4">
          <Pagination>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={page <= 1}
                  onPress={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Назад
                </Pagination.Previous>
              </Pagination.Item>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <Pagination.Item key={p}>
                  <Pagination.Link
                    isActive={page === p}
                    onPress={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Link>
                </Pagination.Item>
              ))}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={page >= totalPages}
                  onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Вперёд
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </div>
      )}
    </div>
  );
}
